import { z } from 'zod';
import { DOCUMENT_KIND } from './constants.ts';

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

/**
 * Grundformen ohne Vorgaben: `.default()` überlebt `.partial()`, ein PATCH würde
 * sonst nicht gesendete Felder auf die Vorgabe zurücksetzen.
 */
const siteFields = z.object({
  code: z
    .string()
    .trim()
    .regex(/^[A-Z0-9]{2,8}$/, 'Kürzel: 2 bis 8 Großbuchstaben oder Ziffern, z. B. FRD.'),
  name: z.string().trim().min(2).max(80),
  street: optionalText(120),
  zip: optionalText(10),
  city: optionalText(80),
  isActive: z.boolean(),
});

export const siteInputSchema = siteFields.extend({
  isActive: siteFields.shape.isActive.default(true),
});
export type SiteInput = z.infer<typeof siteInputSchema>;

export const siteUpdateSchema = siteFields.partial();

const areaFields = z.object({
  siteId: z.uuid(),
  code: z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9-]{1,16}$/, 'Kürzel: bis 16 Zeichen, nur Buchstaben, Ziffern, Bindestrich.'),
  name: z.string().trim().min(2).max(80),
  sortOrder: z.number().int().min(0).max(9999),
  isActive: z.boolean(),
  /** Zuständiges Team für neue Meldungen aus diesem Bereich; leer = Standard-Team des Werks. */
  defaultTeamId: z.uuid().nullable().optional(),
});

export const areaInputSchema = areaFields.extend({
  sortOrder: areaFields.shape.sortOrder.default(100),
  isActive: areaFields.shape.isActive.default(true),
});
export type AreaInput = z.infer<typeof areaInputSchema>;

export const areaUpdateSchema = areaFields.omit({ siteId: true }).partial();

/** Stammdaten einer Maschine (F8). Inventarnummer ist je Standort eindeutig. */
export const machineInputSchema = z.object({
  siteId: z.uuid(),
  areaId: z.uuid(),
  inventoryNo: z.string().trim().min(1, 'Inventarnummer fehlt.').max(40),
  name: z.string().trim().min(2, 'Name fehlt.').max(120),
  manufacturer: optionalText(80),
  model: optionalText(80),
  serialNo: optionalText(80),
  yearBuilt: z.number().int().min(1900).max(2100).nullable().optional(),
  responsibleUserId: z.uuid().nullable().optional(),
  safetyNote: optionalText(1000),
  notes: optionalText(4000),
  inforAssetNo: optionalText(40),
});
export type MachineInput = z.infer<typeof machineInputSchema>;

export const machineUpdateSchema = machineInputSchema.partial().extend({
  version: z.number().int().optional(),
});

export const machineListQuerySchema = z.object({
  siteId: z.uuid().optional(),
  areaId: z.uuid().optional(),
  q: z.string().trim().max(100).optional(),
  includeDecommissioned: z.coerce.boolean().optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(500).default(50),
});

/** Eine Zeile des CSV-Imports (F13). Spaltennamen siehe `machines/import.ts`. */
export const machineImportRowSchema = z.object({
  siteCode: z.string().trim().min(2),
  areaCode: z.string().trim().min(1),
  inventoryNo: z.string().trim().min(1).max(40),
  name: z.string().trim().min(2).max(120),
  manufacturer: optionalText(80),
  model: optionalText(80),
  serialNo: optionalText(80),
  yearBuilt: z.coerce.number().int().min(1900).max(2100).nullable().optional(),
});
export type MachineImportRow = z.infer<typeof machineImportRowSchema>;

export const machineImportPreviewSchema = z.object({ csv: z.string().min(10).max(2_000_000) });
export const machineImportCommitSchema = machineImportPreviewSchema.extend({
  fingerprint: z.string({ error: 'Bitte zuerst die Vorschau erstellen.' }),
});

export const documentUrlInputSchema = z.object({
  kind: z.enum(DOCUMENT_KIND),
  title: z.string().trim().min(2).max(160),
  url: z.url({ protocol: /^https?$/ }),
  validFrom: z.iso.date().nullable().optional(),
});
export type DocumentUrlInput = z.infer<typeof documentUrlInputSchema>;

/** Kurzcode auf dem QR-Etikett: 6 Zeichen Crockford-Base32 ohne I, L, O, U (F11). */
export const QR_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
export const qrCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[0-9A-HJKMNP-TV-Z]{6}$/, 'Ungültiger Maschinencode.');
